import React, { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import FocusRing from '@atlaskit/focus-ring';
import mergeRefs from '../../../../utils/merge-refs.tsx';
import useLazyCallback from '../../../../utils/use-lazy-callback.tsx';
import useStateRef from '../../../../utils/use-state-ref.tsx';
import { keylineHeight, keylineColor } from './constants.tsx';
import { rafSchedule } from './utils.tsx';

// Based on atlassian-frontend/packages/design-system/modal-dialog/src/internal/components/scroll-container.tsx
type ScrollContainerProps = {
	/**
	 * Children of the body within modal dialog.
	 */
	children: ReactNode;
	/**
	 * A `testId` prop is provided for specified elements,
	 * which is a unique string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * Allow user to disable showing the keylines even when scrolling.
	 */
	disableAutoTopKeyline?: boolean;
	disableAutoBottomKeyline?: boolean;
};

/**
 * A container that shows top and bottom keylines when the
 * content overflows into the scrollable element.
 */
const ScrollContainer = forwardRef<HTMLDivElement, ScrollContainerProps>(
	(props: ScrollContainerProps, ref) => {
		const {
			testId,
			children,
			disableAutoTopKeyline = true,
			disableAutoBottomKeyline = true,
		} = props;
		const [hasSiblings, setSiblings] = useStateRef({
			previous: false,
			next: false,
		});

		const [showContentFocus, setContentFocus] = useState(false);
		const [showTopKeyline, setTopKeyline] = useState(false);
		const [showBottomKeyline, setBottomKeyline] = useState(false);

		const scrollableRef = useRef<HTMLDivElement>(null);

		const setLazySiblings = useLazyCallback(setSiblings);

		const setLazyContentFocus = useLazyCallback(
			rafSchedule(() => {
				const target = scrollableRef.current;
				target && setContentFocus(target.scrollHeight > target.clientHeight);
			}),
		);

		const setLazyKeylines = useLazyCallback(
			rafSchedule(() => {
				const target = scrollableRef.current;
				if (target) {
					const scrollableDistance = target.scrollHeight - target.clientHeight;

					if (hasSiblings.current.previous) {
						setTopKeyline(target.scrollTop > keylineHeight);
					}

					if (hasSiblings.current.next) {
						setBottomKeyline(target.scrollTop <= scrollableDistance - keylineHeight);
					}
				}
			}),
		);

		useEffect(() => {
			const target = scrollableRef.current;

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.addEventListener('resize', setLazyKeylines, false);
			target?.addEventListener('scroll', setLazyKeylines, false);

			setLazyContentFocus();
			setLazyKeylines();
			setLazySiblings({
				previous: Boolean(target?.previousElementSibling),
				next: Boolean(target?.nextElementSibling),
			});

			return () => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.removeEventListener('resize', setLazyKeylines, false);
				target?.removeEventListener('scroll', setLazyKeylines, false);
			};
		}, [setLazyContentFocus, setLazyKeylines, setLazySiblings]);
		return (
			<FocusRing isInset>
				<ScrollContainerWrapper
					// eslint-disable-next-line styled-components-a11y/no-noninteractive-tabindex
					tabIndex={showContentFocus ? 0 : undefined}
					data-testid={testId !== null && testId !== undefined && `${testId}--scrollable`}
					innerRef={mergeRefs([ref, scrollableRef])}
					showTopKeyline={!disableAutoTopKeyline && showTopKeyline}
					showBottomKeyline={!disableAutoBottomKeyline && showBottomKeyline}
				>
					{children}
				</ScrollContainerWrapper>
			</FocusRing>
		);
	},
);

ScrollContainer.displayName = 'ScrollContainer';

export default ScrollContainer;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScrollContainerWrapper = styled.div.attrs<{
	showTopKeyline: boolean;
	showBottomKeyline: boolean;
}>({
	// This is a false positive: https://product-fabric.atlassian.net/browse/USS-49
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	style: ({
		showTopKeyline,
		showBottomKeyline,
	}: {
		showTopKeyline: boolean;
		showBottomKeyline: boolean;
	}) => ({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderTop: `${showTopKeyline ? `${keylineHeight}px solid ${keylineColor}` : 'none'}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderBottom: `${showBottomKeyline ? `${keylineHeight}px solid ${keylineColor}` : 'none'}`,
	}),
})`
	display: inherit;
	margin: 0;

	flex: inherit;
	flex-direction: inherit;
	flex-grow: 1;
	overflow-x: hidden;
	overflow-y: auto;

	@media (min-width: 480px) {
		height: unset;
		overflow-y: auto;
	}
`;
